import { useEffect, useState } from 'react';
import { getImportRunsForBrand } from '../../services/firebase';
import type { ImportRunRecord } from '../../types';
import { describeError } from './errorUtils';

interface Props {
  brandId: string;
  /** Sources to show labels for. */
  sourceLabels?: Record<string, string>;
  /** Bump this to force a refetch (e.g. after a new import). */
  refreshKey?: number;
}

const STATUS_STYLES: Record<ImportRunRecord['status'], string> = {
  success: 'bg-green-100 text-green-700',
  partial: 'bg-amber-100 text-amber-700',
  error: 'bg-red-100 text-red-700',
};

function formatAt(ts: ImportRunRecord['at']): string {
  if (!ts) return '—';
  try {
    const d = (ts as { toDate?: () => Date }).toDate?.() ?? new Date();
    return d.toLocaleString();
  } catch {
    return '—';
  }
}

export function ImportHistoryPanel({ brandId, sourceLabels, refreshKey }: Props) {
  const [runs, setRuns] = useState<ImportRunRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getImportRunsForBrand(brandId)
      .then((rows) => {
        if (!cancelled) setRuns(rows);
      })
      .catch((err) => {
        console.error('Failed to load import runs:', err);
        if (!cancelled) {
          setError(describeError(err));
          setRuns(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, refreshKey]);

  if (loading && !runs) {
    return (
      <div className="text-xs text-gray-500 px-3 py-2">Loading history…</div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
        Couldn&apos;t load history — {error}
      </div>
    );
  }

  if (!runs || runs.length === 0) {
    return (
      <div className="text-xs text-gray-400 px-3 py-2">No imports yet.</div>
    );
  }

  return (
    <div className="text-xs">
      <table className="w-full">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-1.5 pr-2">When</th>
            <th className="py-1.5 pr-2">Source</th>
            <th className="py-1.5 pr-2">Status</th>
            <th className="py-1.5 pr-2 text-right">Imported</th>
            <th className="py-1.5 pr-2 text-right">Updated</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id} className="border-b border-gray-100 align-top">
              <td className="py-1.5 pr-2 text-gray-600 whitespace-nowrap">
                {formatAt(r.at)}
              </td>
              <td className="py-1.5 pr-2 text-gray-700">
                {sourceLabels?.[r.sourceId] ?? r.sourceKind}
              </td>
              <td className="py-1.5 pr-2">
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_STYLES[r.status]}`}
                >
                  {r.status}
                </span>
                {r.errorMessage && (
                  <div className="text-[10px] text-red-600 mt-1 max-w-xs">
                    {r.errorMessage}
                  </div>
                )}
              </td>
              <td className="py-1.5 pr-2 text-right text-gray-700">
                {r.importedCount}
              </td>
              <td className="py-1.5 pr-2 text-right text-gray-700">
                {r.updatedCount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
